import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useNotification } from "../../../../context/NotificationContext";
import PushToggle from "../../../PushToggle/PushToggle";
import NotificationAlertToggles from "../../../NotificationAlertToggles/NotificationAlertToggles";
import { translateLabel } from "../../../../i18n/defaultLabels";

const TYPE_ICONS = {
  assigned: "assignment_ind",
  statusChanged: "sync_alt",
  deadline: "alarm",
};

// Title and body in the app's language, built from the notification's type
// and params. Old notifications (without params) show their stored text.
const getNotificationText = (notification, t) => {
  const { type, params } = notification;
  if (!params) return { title: notification.title, body: notification.body };

  let title = notification.title;
  if (type === "assigned") {
    title = t("notifications.types.assigned", params);
  } else if (type === "statusChanged") {
    title = t("notifications.types.statusChanged", {
      ...params,
      status: translateLabel(params.status, t),
    });
  } else if (type === "deadline") {
    title =
      params.when === "tomorrow"
        ? t("notifications.types.deadlineTomorrow")
        : t("notifications.types.deadlineToday");
  }

  return { title, body: params.taskTitle ?? notification.body };
};

const formatTimeAgo = (date, t, language) => {
  const minutes = Math.floor((Date.now() - new Date(date)) / 60000);
  if (minutes < 1) return t("time.justNow");
  if (minutes < 60) return t("time.minutesAgo", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("time.hoursAgo", { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t("time.daysAgo", { count: days });
  return new Date(date).toLocaleDateString(language, {
    month: "short",
    day: "numeric",
  });
};

function NotificationsList({ onClose }) {
  const { notifications, unreadCount, markAllAsRead, openNotification } =
    useNotification();
  const { t, i18n } = useTranslation();
  const [openingId, setOpeningId] = useState(null);

  const handleClick = async (notification) => {
    if (openingId) return;
    setOpeningId(notification._id);
    try {
      await openNotification(notification);
      if (notification.taskId && onClose) onClose();
    } catch (err) {
      console.error("Error opening notification:", err);
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className="notifications-list">
      {notifications.length === 0 ? (
        <div className="notifications-modal__empty">
          <span className="material-icons">notifications_none</span>
          <p>{t("notifications.empty")}</p>
          <span className="notifications-modal__empty-subtitle">
            {t("notifications.emptyHint")}
          </span>
        </div>
      ) : (
        <>
          {unreadCount > 0 && (
            <div className="notifications-list__actions">
              <button onClick={markAllAsRead}>
                {t("notifications.markAllRead")}
              </button>
            </div>
          )}
          <div className="notifications-modal__list">
            {notifications.map((notification) => {
              const { title, body } = getNotificationText(notification, t);
              return (
                <div
                  key={notification._id}
                  className={`notifications-list__item ${notification.isRead ? "" : "is-unread"} ${openingId === notification._id ? "is-opening" : ""}`}
                  onClick={() => handleClick(notification)}
                >
                  <span className="material-icons notifications-list__icon">
                    {TYPE_ICONS[notification.type] || "notifications"}
                  </span>
                  <div className="notifications-list__text">
                    <span className="notifications-list__title">{title}</span>
                    {body && (
                      <span className="notifications-list__body">{body}</span>
                    )}
                    <span className="notifications-list__time">
                      {formatTimeAgo(notification.createdAt, t, i18n.language)}
                    </span>
                  </div>
                  {!notification.isRead && (
                    <span className="notifications-list__unread-dot" />
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="notifications-list__push">
        <PushToggle />
        <NotificationAlertToggles />
      </div>
    </div>
  );
}

export default NotificationsList;
